if (!customElements.get('nosto-collection')) {
  class NostoCollection extends HTMLElement {
    constructor() {
      super();

      this.currentPage = 1;
      this.sortByValues = {
        data: [
          { value: 'best-selling', label: 'Best Selling' },
          { value: 'name-asc', label: 'A-Z' },
          { value: 'name-desc', label: 'Z-A' },
          { value: 'price-asc', label: 'Price, low to high' },
          { value: 'price-desc', label: 'Price, high to low' },
          { value: 'created-asc', label: 'Date, old to new' },
          { value: 'created-desc', label: 'Date, new to old' },
          { value: 'manual', label: 'Manual', selected: true },
        ],
        field: 'sort_by',
        name: 'Sort by',
        type: 'terms',
      };
    }
    connectedCallback() {
      this.getNostoProducts(false).then(() => {
        this.getNostoProducts(true);
      });

      document.addEventListener('nosto:products-reloaded', (e) => {
        e.preventDefault();
        this.currentPage = 1;
        this.getNostoProducts(true);
      });
    }

    get collectionId() {
      return this.dataset.collectionId;
    }

    get productsPerPage() {
      return parseInt(this.dataset.productsPerPage) || 12;
    }

    get productsGrid() {
      const grid = this.querySelector('#product-grid');
      if (!grid) console.warn('Missing #product-grid inside <nosto-collection>');
      return grid;
    }

    get emptyState() {
      return this.querySelector('.collection--empty');
    }

    get collectionPromoBlocksTemplate() {
      return this.querySelector('#collection-promo-blocks-template');
    }

    updateSortByValues(sortParam) {
      this.sortByValues.data.forEach((option) => {
        option.selected = option.value === sortParam;
      });
    }

    getNostoProducts(applyFilters = true, pagination = false) {
      return new Promise((resolve, reject) => {
        const params = {
          products: {
            categoryId: this.collectionId,
            fields: ['url'],
            facets: ['*'],
            size: this.productsPerPage,
            from: (this.currentPage - 1) * this.productsPerPage,
          },
        };

        if (applyFilters) {
          const urlParams = new URLSearchParams(window.location.search);
          const filterMap = {};

          for (const [key, value] of urlParams.entries()) {
            if (key === 'sort_by' || key === 'price_from' || key === 'price_to') continue;
            if (!filterMap[key]) filterMap[key] = [];
            filterMap[key].push(value);
          }

          const filters = Object.entries(filterMap).map(([field, value]) => ({
            field,
            value,
          }));

          if (filters.length > 0) {
            params.products.filter = filters;
          }

          // Price filter
          const minPrice = urlParams.get('price_from');
          const maxPrice = urlParams.get('price_to');
          if (minPrice || maxPrice) {
            params.products.filter = params.products.filter || [];
            params.products.filter.push({
              field: 'price',
              range: {
                gte: minPrice || '0',
                lte: maxPrice || '100',
              },
            });
          }

          // Sorting
          const sortParam = urlParams.get('sort_by');
          if (sortParam) {
            const [field, order] = sortParam.split('-');
            params.products.sort = {
              field,
              order: order === 'desc' ? 'desc' : 'asc', // fallback to asc if invalid
            };
            this.updateSortByValues(sortParam);
          }
        }

        nostojs(async (api) => {
          try {
            const response = await api.search(params, { track: 'category', redirect: true });
            const products = response.products;
            const productHits = products.hits;
            const productsRendered = products.from + products.size;
            const paginate = productsRendered < products.total;

            this.renderProducts(productHits, pagination);
            this.renderCollectionPromoBlocks();
            this.updateEmptyState(productHits);
            this.appendPagination(paginate);
            products.facets.unshift(this.sortByValues);

            const event = new CustomEvent('nosto:products-loaded', {
              detail: {
                products,
                initial: !applyFilters,
              },
              bubbles: true,
            });
            this.dispatchEvent(event);

            resolve();
          } catch (error) {
            console.error('Error fetching Nosto products:', error);
            reject(error);
          }
        });
      });
    }

    updateEmptyState(products) {
      if (!products.length) {
        this.emptyState.classList.remove('hidden');
        this.productsGrid.classList.add('hidden');
        this.productsGrid.innerHTML = '';
      } else {
        this.emptyState.classList.add('hidden');
        this.productsGrid.classList.remove('hidden');
      }
    }

    renderProducts(products, pagination) {
      const productMarkup = products
        .map((product, index) => {
          const productHandle = product.url.split('/').pop();
          return `
            <li class="grid__item" data-index="${index + 1}">
              <nosto-product-card data-handle="${productHandle}" id="CardWrapper-${product.productId}" class="card-wrapper"></nosto-product-card>
            </li>
          `;
        })
        .join('');

      if (pagination) {
        this.productsGrid.insertAdjacentHTML('beforeend', productMarkup);
      } else {
        this.productsGrid.innerHTML = productMarkup;
      }
    }

    renderCollectionPromoBlocks() {
      const collectionPromoBlocks = this.collectionPromoBlocksTemplate.content.cloneNode(true);
      const promoBlocks = collectionPromoBlocks.querySelectorAll('.grid__item');
      promoBlocks.forEach((promoBlock) => {
        const index = promoBlock.getAttribute('data-index');
        const gridItem = this.productsGrid.querySelector(`.grid__item[data-index="${index}"]`);
        console.log(gridItem);
        if (gridItem) {
          if (gridItem.classList.contains('grid__item--promo-block')) return;
          gridItem.insertAdjacentElement('beforebegin', promoBlock);
        }
      });
    }

    appendPagination(paginate) {
      const paginationContainer = this.querySelector('.pagination-container');

      if (!paginate) {
        if (paginationContainer) paginationContainer.remove();
        return;
      }
      const pagination = document.createElement('div');
      pagination.classList.add('pagination-container');
      pagination.innerHTML = `
        <button type="button" class="button button--primary">${window.checkoutStrings.expandNoticeLabel || 'View more'}</button>
      `;
      if (paginationContainer) paginationContainer.replaceWith(pagination);

      this.productsGrid.insertAdjacentElement('afterend', pagination);

      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.currentPage += 1;
            this.getNostoProducts(true, true);
          }
        },
        {
          rootMargin: '300px',
          threshold: 0,
        }
      );

      this.observer.observe(pagination);

      const loadMoreButton = pagination.querySelector('button');
      loadMoreButton?.addEventListener('click', () => {
        this.currentPage += 1;
        this.getNostoProducts(true, true);
      });
    }
  }

  customElements.define('nosto-collection', NostoCollection);
}
